/**
 * Dates, plurals, and the words a count has to agree with. Ported whole from
 * `legacy/frontend/src/format.ts` at legacy commit 2953e3f — every function, every comment,
 * the declaration order — because `.claude/rules/copy.md` asks for this file before any
 * screen that formats one of its values: its rules are shared and subtle, and re-deriving
 * them per screen is how they drift.
 *
 * **This file can never join `scripts/verbatim.txt`.** Legacy's line 1 is one import from
 * `./api`, and here it has to be two, because that line mixes two kinds of thing. The four
 * server shapes — `Birthday`, `LiveRow`, `SessionKind`, `VoicePart` — come from
 * `@contracts/index`, since nothing else may define one (ADR-0011); `LiveRow` is in the
 * contract ahead of the flow that owns it for exactly this reason (FND-033). `ApiError` is
 * not a server shape at all: it is the client's own class (`app/src/api.ts:46`), a **value**,
 * read by `failureText` below as `error instanceof ApiError`, and it does not belong in the
 * contract. A value import cannot share a line with a type-only import, so this file is not
 * byte-identical to its original and the byte-identity check would fail it. That is the whole
 * of the difference — nothing else below was touched.
 */

import { ApiError } from './api';
import type { Birthday, LiveRow, SessionKind, VoicePart } from '@contracts/index';

const pad = (value: number): string => String(value).padStart(2, '0');

const VOICE_PART_NAMES: Record<VoicePart, string> = {
  soprano: 'сопрано',
  alto: 'альт',
  tenor: 'тенор',
  bass: 'бас',
};

export function voicePartName(part: VoicePart | null): string {
  // «ждёт партию» and not «без партии» since 2026-08-24: a part is the regent's
  // to give, so somebody without one is not a person who declined to have a
  // voice - they are a person the regent has not got to yet, and the roster is
  // where he finds that out. There is no way to arrive back at this state: the
  // segments that set a part have no fifth option any more.
  return part === null ? 'ждёт партию' : VOICE_PART_NAMES[part];
}

/**
 * One letter, for the places where a whole word would cost a column: after a
 * name in the regent's live roster, and under a figure in the counter.
 */
const VOICE_PART_LETTERS: Record<VoicePart, string> = {
  soprano: 'С',
  alto: 'А',
  tenor: 'Т',
  bass: 'Б',
};

export function voicePartLetter(part: VoicePart | null): string {
  return part === null ? '—' : VOICE_PART_LETTERS[part];
}

/**
 * The words for a kind of gathering, in the cases the copy actually needs.
 *
 * Russian is why this is a table rather than one noun. «Спевка» is feminine and
 * «участие» is neuter, so nothing agreeing with them can be written once: «одна
 * спевка» against «одно участие», «Спевка началась» against «Участие
 * началось». The screens dodge the verb agreement where they can — «Начало в
 * 18:10» rather than «Началась в 18:10» — and take the noun from here
 * everywhere else.
 *
 * Where the kind is not known, because a list holds both, the copy says neither
 * word and finds a neutral wording instead. That is the owner's call of
 * 2026-08-19: a word per kind wherever the kind is known, and no invented
 * umbrella term on screen.
 */
export type EventWords = {
  /** «спевка» / «участие» — the subject of a sentence. */
  nominative: string;
  /** «Спевка» / «Участие» — a title, or the first word of one. */
  titled: string;
  /** «спевку» / «участие» — after «закрыть», «открыть», «убрать». */
  accusative: string;
  /** «спевки» / «участия» — after «до начала», «нет». */
  genitive: string;
  /** «спевке» / «участии» — after «на», «о». */
  prepositional: string;
  /** «спевок» / «участий» — after a count of five or more. */
  genitivePlural: string;
  /**
   * «закрыта» / «закрыто» — a past participle agreeing with `titled`.
   *
   * The two kinds are different genders, so any sentence in the past has to
   * carry its verb here rather than hard-code one ending: «Спевка закрыта» and
   * «Участие закрыто» cannot be written as one string with a word substituted
   * into it, which is what produced «спевка не началось».
   */
  closedForm: string;
  /**
   * «не началась» / «не началось» — the same agreement, negated.
   *
   * The sentence «Ответ можно поменять, пока спевка не началось.» stood in the
   * app until 2026-08-21: one string with the noun substituted into it, and a
   * verb frozen in whichever gender the person who wrote it had in mind.
   */
  startedForm: string;
  /** The three forms a count agrees with: «1 спевка, 2 спевки, 5 спевок». */
  counted: [string, string, string];
};

const REHEARSAL: EventWords = {
  nominative: 'спевка',
  titled: 'Спевка',
  accusative: 'спевку',
  genitive: 'спевки',
  prepositional: 'спевке',
  genitivePlural: 'спевок',
  closedForm: 'закрыта',
  startedForm: 'не началась',
  counted: ['спевка', 'спевки', 'спевок'],
};

const SERVICE: EventWords = {
  nominative: 'участие',
  titled: 'Участие',
  accusative: 'участие',
  genitive: 'участия',
  prepositional: 'участии',
  genitivePlural: 'участий',
  closedForm: 'закрыто',
  startedForm: 'не началось',
  counted: ['участие', 'участия', 'участий'],
};

export function words(kind: SessionKind): EventWords {
  return kind === 'service' ? SERVICE : REHEARSAL;
}

/**
 * «спевку» → «Спевку», for a sentence that starts with a case the table holds
 * in lower case. Cheaper than a second column of the same six words, and the
 * only alternative - `text-transform` on a `::first-letter` - would capitalise
 * whatever happened to wrap onto the first line.
 */
export function capitalised(word: string): string {
  return word.charAt(0).toUpperCase() + word.slice(1);
}

/** Genitive, because a birthday is always read as «12 марта» and never «12 март». */
const MONTHS_OF = [
  'января',
  'февраля',
  'марта',
  'апреля',
  'мая',
  'июня',
  'июля',
  'августа',
  'сентября',
  'октября',
  'ноября',
  'декабря',
];

/** How many days each month has, February at 29: a leap-day birthday is real. */
export const DAYS_IN_MONTH = [31, 29, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];

/** «12 марта». No year anywhere in this app - see backend/app/domain/birthdays.py. */
export function formatBirthday(birthday: Birthday): string {
  return `${birthday.day} ${MONTHS_OF[birthday.month - 1]}`;
}

/** The month on its own, for the picker: «март», nominative. */
export const MONTH_NAMES = [
  'январь',
  'февраль',
  'март',
  'апрель',
  'май',
  'июнь',
  'июль',
  'август',
  'сентябрь',
  'октябрь',
  'ноябрь',
  'декабрь',
];

/** «с сентября»: the month a stretch of time began in. `month` is 1-based. */
export function monthOf(month: number): string {
  return MONTHS_OF[month - 1];
}

/**
 * «в августе»: a month named as the place something happened, which is the one
 * case the app needed that neither the genitive above nor the picker's
 * nominative can stand in for — «пауза в августе» against «пауза с августа»,
 * one of which is a whole month and the other the start of a stretch.
 */
const MONTHS_IN = [
  'январе',
  'феврале',
  'марте',
  'апреле',
  'мае',
  'июне',
  'июле',
  'августе',
  'сентябре',
  'октябре',
  'ноябре',
  'декабре',
];

export function monthIn(month: number): string {
  return MONTHS_IN[month - 1];
}

/** Russian plural agreement after a number: «1 спевку», «2 спевки», «5 спевок». */
export function plural(count: number, forms: [string, string, string]): string {
  const teens = count % 100;
  if (teens >= 11 && teens <= 14) return forms[2];
  const last = count % 10;
  if (last === 1) return forms[0];
  if (last >= 2 && last <= 4) return forms[1];
  return forms[2];
}

export function formatTime(iso: string): string {
  return HOUR_MINUTE.format(new Date(iso));
}

/**
 * Whether being on time outlasts the start, so «вовремя до» has something to add.
 *
 * A gathering whose code went up well before its hour spends the whole grace on
 * the waiting: the deadline lands on the start itself, and a line naming both
 * prints the same minute twice. The two moments come from the server already
 * decided - this only asks whether they differ.
 */
export function hasLeeway(session: {
  effective_start: string;
  on_time_until: string;
}): boolean {
  return new Date(session.on_time_until).getTime() > new Date(session.effective_start).getTime();
}

/**
 * «чт» — the weekday in the two letters Russian abbreviates it to.
 *
 * The dot the locale puts after it goes: these stand in front of a date that
 * carries dots of its own, and «чт. 21.08» reads as three of them in a row.
 */
export function weekdayShort(when: Date): string {
  return WEEKDAY_SHORT.format(when).replace('.', '');
}

/**
 * C-023 batch 3 (`FND-1618`): one formatter per shape, built once. `toLocaleDateString` and
 * `toLocaleTimeString` build a new `Intl.DateTimeFormat` on every call, and the date wheel on
 * «Запланировать» labels a year of days through `weekdayShort`: 78 ms of the 115 ms of script
 * behind that tap on the audit's rig, before the sheet could move. The other three are the same
 * cost on every row of a list. The same locale and the same options, so the same words.
 */
const WEEKDAY_SHORT = new Intl.DateTimeFormat('ru-RU', { weekday: 'short' });
const HOUR_MINUTE = new Intl.DateTimeFormat('ru-RU', { hour: '2-digit', minute: '2-digit' });
const DAY_MONTH_LONG = new Intl.DateTimeFormat('ru-RU', { day: 'numeric', month: 'long' });
const DAY_MONTH_NUMERIC = new Intl.DateTimeFormat('ru-RU', { day: '2-digit', month: '2-digit' });

/**
 * «чт 21.08» — the weekday and the date, monospaced, over a gathering's title.
 *
 * Numeric on purpose: this line stands at the head of a column of cards and its
 * job is to be compared with the one above it, which a month spelled out in
 * letters cannot do at a glance.
 */
export function formatWeekdayDate(iso: string): string {
  const when = new Date(iso);
  return `${weekdayShort(when)} ${pad(when.getDate())}.${pad(when.getMonth() + 1)}`;
}

/** The month cut to three letters, for a row of the date wheel where the day,
    the weekday and sometimes a year share one column. */
export const MONTHS_SHORT = [
  'янв',
  'фев',
  'мар',
  'апр',
  'мая',
  'июн',
  'июл',
  'авг',
  'сен',
  'окт',
  'ноя',
  'дек',
];

/**
 * «чт 27 авг», and «чт 14 янв 2027» when the year is not the current one — one
 * row of the wheel that picks the date of a gathering.
 *
 * The weekday is in it because this choir rehearses on Thursdays and sings on
 * Sundays, so the day of the week is the thing being chosen and the number is
 * the consequence. The year is a word rather than a column of its own: the
 * wheel runs over real future dates, so there is no past year to reach and
 * nothing to scroll a year for.
 */
export function formatWheelDate(when: Date): string {
  const year = when.getFullYear() === new Date().getFullYear() ? '' : ` ${when.getFullYear()}`;
  return `${weekdayShort(when)} ${when.getDate()} ${MONTHS_SHORT[when.getMonth()]}${year}`;
}

/** "21 августа" — a date inside a sentence about one rehearsal. */
export function formatLongDate(iso: string): string {
  return DAY_MONTH_LONG.format(new Date(iso));
}

/**
 * «Четверг, 4 сентября, 18:00» — a date somebody has to be somewhere on.
 *
 * The whole weekday and the whole month, unlike every other date in this app:
 * this one stands as a card's title rather than in a line of figures, and a
 * title is read once and remembered rather than scanned down a column. It is
 * the same shape the bot says the date in, for the same reason.
 */
const WEEKDAY_FULL = [
  'Воскресенье',
  'Понедельник',
  'Вторник',
  'Среда',
  'Четверг',
  'Пятница',
  'Суббота',
];

export function formatAppointment(iso: string): string {
  const when = new Date(iso);
  return (
    `${WEEKDAY_FULL[when.getDay()]}, ${when.getDate()} ${MONTHS_OF[when.getMonth()]}, ` +
    formatTime(iso)
  );
}

/**
 * «14 и 21 августа» — the dates of the misses that made the bar.
 *
 * The month is printed once when both fall inside it, because that is how the
 * sentence is said out loud and the repetition is what makes a list of dates
 * read as a form rather than as a fact. One date is just the date.
 *
 * **A day appears once however many gatherings it held.** A choir can hold two
 * in a day - a morning and an evening - and missing both used to print
 * «Пропущены 21 и 21 августа», which reads as a bug in the app rather than as a
 * fact about the week. The count is not lost by this: the label over the
 * sentence already says «две спевки подряд», so the dates answer «when» and the
 * label answers «how many». Deduplicated by the reader's own day, the same
 * calendar the dates are printed in.
 */
export function joinDates(isos: string[]): string {
  const seen = new Set<string>();
  const dates: Date[] = [];
  for (const iso of isos) {
    const when = new Date(iso);
    const day = `${when.getFullYear()}-${when.getMonth()}-${when.getDate()}`;
    if (seen.has(day)) continue;
    seen.add(day);
    dates.push(when);
  }
  if (dates.length === 0) return '';
  const month = dates[0].getMonth();
  const sameMonth = dates.every((when) => when.getMonth() === month);
  const parts = dates.map((when, index) =>
    sameMonth && index < dates.length - 1
      ? String(when.getDate())
      : DAY_MONTH_LONG.format(when),
  );
  return parts.length === 1 ? parts[0] : `${parts.slice(0, -1).join(', ')} и ${parts.at(-1)}`;
}

/**
 * «две спевки подряд» — the rule's own phrase, wherever it is said.
 *
 * In words up to four and in figures after that: the roster's sub-line, the
 * card's label and the bot all say the same thing, and a choir where somebody
 * has missed five in a row has stopped being a case the wording is for.
 *
 * It said «два пропуска подряд» for one evening, 2026-08-24, because the phrase
 * does not say whether the two rehearsals were attended or missed and the owner
 * read it as the first. Taken back the same evening together with the matrix's
 * dates: the wording goes to the design pass those two are waiting for rather
 * than being changed twice in an hour. The ambiguity is real and is written
 * down in `PLAN.md`.
 */
const RUN_WORDS = ['одна', 'две', 'три', 'четыре'];

export function missesInARow(count: number): string {
  const many = plural(count, ['спевка', 'спевки', 'спевок']);
  const word = RUN_WORDS[count - 1];
  return `${word ?? count} ${many} подряд`;
}

/**
 * The initials in an avatar: «Мария Лебедева» → «МЛ».
 *
 * Two letters at most, and one when that is all the name has: a roster is
 * scanned by shape before it is read, and three letters in a 38px circle is
 * neither.
 */
export function initials(name: string): string {
  const words = name.trim().split(/\s+/).filter((word) => word !== '');
  if (words.length === 0) return '?';
  return words
    .slice(0, 2)
    .map((word) => word[0].toUpperCase())
    .join('');
}

/** "14.08" — a date where a whole one is needed and there is no room to spell it. */
export function formatDayMonth(iso: string): string {
  return DAY_MONTH_NUMERIC.format(new Date(iso));
}

/** "14" — a matrix column, sixteen pixels wide, in a row that repeats the month. */
export function formatDay(iso: string): string {
  return pad(new Date(iso).getDate());
}

/* "АВГ" — the month over a run of matrix columns.

   Three letters off the picker's nominative names and not `MONTHS_SHORT`, which
   is the genitive a date row needs — «27 авг», «21 мая». This label stands on
   its own over a group of columns, where «МАЯ» reads as half of a date whose
   day has gone missing. No full stop: the strip keeps one slot of a fixed width
   for whatever this returns. */
function formatMonthLabel(iso: string): string {
  return MONTH_NAMES[new Date(iso).getMonth()].slice(0, 3).toUpperCase();
}

/** The month of every matrix column that starts one, and `null` for the rest —
    the month once over its group of columns rather than once under each day.

    All `null` when the window holds a single month: a choir whose ten
    rehearsals are all in August is told nothing by «АВГ» over the first of
    them, and the strip stays the bare row of days it has always been.

    The month comes off a `Date` rather than off the ISO string, because the day
    printed in the column comes off a `Date` too — comparing the two by text
    would put the boundary on the wrong column for any rehearsal whose local
    date and UTC date disagree. */
export function monthLabels(starts: string[]): (string | null)[] {
  const month = (iso: string): number => {
    const when = new Date(iso);
    return when.getFullYear() * 12 + when.getMonth();
  };
  const crosses = starts.some((start) => month(start) !== month(starts[0]));
  if (!crosses) return starts.map(() => null);
  return starts.map((start, index) =>
    index === 0 || month(start) !== month(starts[index - 1]) ? formatMonthLabel(start) : null,
  );
}


/** "today", "tomorrow" or a weekday — so nobody has to do date arithmetic. */
export function relativeDay(iso: string): string | null {
  const startOfDay = (value: Date): number =>
    new Date(value.getFullYear(), value.getMonth(), value.getDate()).getTime();
  const days = Math.round(
    (startOfDay(new Date(iso)) - startOfDay(new Date())) / (24 * 60 * 60 * 1000),
  );
  if (days === 0) return 'сегодня';
  if (days === 1) return 'завтра';
  if (days > 1 && days < 7) return 'через ' + days + ' дн.';
  return null;
}

/**
 * «сегодня», «завтра» or «в четверг» — how a choir says when it next meets.
 *
 * Different from `relativeDay`, which counts days for a date line: this one
 * goes inside a sentence, so «через 4 дн.» would not fit and the weekday is
 * what anybody actually plans by.
 */
const WEEKDAY_IN = [
  'в воскресенье',
  'в понедельник',
  'во вторник',
  'в среду',
  'в четверг',
  'в пятницу',
  'в субботу',
];

export function whenNextDay(iso: string): string {
  const near = relativeDay(iso);
  if (near === 'сегодня' || near === 'завтра') return near;
  return WEEKDAY_IN[new Date(iso).getDay()];
}

/**
 * A value for input[type=datetime-local] in the browser's local time.
 * toISOString will not do here: it converts to UTC and shifts the time on screen.
 */
export function toLocalInputValue(when: Date): string {
  return (
    `${when.getFullYear()}-${pad(when.getMonth() + 1)}-${pad(when.getDate())}` +
    `T${pad(when.getHours())}:${pad(when.getMinutes())}`
  );
}

/**
 * The arrival time a row already carries, as input[type=time] wants it.
 *
 * Empty unless the lateness was actually measured. A manual «Опоздал» with no
 * time typed still stores marked_at — the moment the regent's finger landed —
 * and offering that back as an arrival would turn the length of his walk down
 * the roster into a number he never claimed, on the next tap of Save.
 */
export function arrivalInputValue(row: LiveRow): string {
  if (row.status !== 'late' || row.marked_at === null || row.late_minutes === null) return '';
  const when = new Date(row.marked_at);
  return `${pad(when.getHours())}:${pad(when.getMinutes())}`;
}

/**
 * A typed time turned into the moment somebody arrived at this rehearsal.
 *
 * Only the time is asked for, because the app knows the day — but the day of
 * the arrival is not always the day of the start. A rehearsal opened at 23:00
 * takes people in at 00:20, and hanging that on the starting date puts it
 * twenty-two hours *before* the rehearsal instead of eighty minutes after. So
 * the time is read as the nearest such moment to the start, either side of it.
 *
 * Null when the field holds nothing that can be read as a time.
 */
export function arrivalIso(sessionStartIso: string, time: string): string | null {
  const typed = /^(\d{1,2}):(\d{2})/.exec(time);
  if (typed === null) return null;
  const start = new Date(sessionStartIso);
  const at = new Date(
    start.getFullYear(),
    start.getMonth(),
    start.getDate(),
    Number(typed[1]),
    Number(typed[2]),
  );
  // A day by the calendar, not 24 hours of milliseconds: the shift has to land
  // on the same wall clock even where the offset changes overnight.
  const halfDayMs = 12 * 60 * 60 * 1000;
  if (at.getTime() - start.getTime() > halfDayMs) at.setDate(at.getDate() - 1);
  else if (start.getTime() - at.getTime() > halfDayMs) at.setDate(at.getDate() + 1);
  return at.toISOString();
}

/**
 * Why a typed arrival time cannot be saved, or null when it can.
 *
 * A time before the rehearsal started is not lateness, and the server says so
 * in its own way: it clamps the difference at zero and the row comes back
 * «опоздал на 0 мин», which is a mark that contradicts itself. The regent typed
 * a real time and got a number that is not about it, so the refusal belongs
 * here - before the request, next to the field.
 */
export function arrivalProblem(sessionStartIso: string, time: string): string | null {
  const at = arrivalIso(sessionStartIso, time);
  if (at === null) return 'Не понял время — введи его как 18:05';
  if (new Date(at).getTime() < new Date(sessionStartIso).getTime()) {
    return `Это раньше начала спевки (${formatTime(sessionStartIso)}) — тогда опоздания не было`;
  }
  return null;
}

/**
 * How long to wait, in words a person can act on.
 *
 * Seconds while there are few of them, minutes once there are many: the pause
 * doubles with every further burst of attempts, so "подожди 960 секунд" is a
 * number nobody converts in their head in the choir loft.
 */
export function waitText(seconds: number): string {
  if (seconds < 60) {
    return `${seconds} ${plural(seconds, ['секунду', 'секунды', 'секунд'])}`;
  }
  const minutes = Math.ceil(seconds / 60);
  return `${minutes} ${plural(minutes, ['минуту', 'минуты', 'минут'])}`;
}

/**
 * What to put on screen when a request failed.
 *
 * The rate limiter answers with the seconds remaining and always has - the
 * server puts retry_after_seconds in the body - but the screen used to show
 * "Слишком много попыток, подождите" and stop there. The wait starts at a
 * minute and doubles, while the code being typed lives 60 to 90 seconds, so
 * mistyping twice is ordinary rather than careless. Without the number the
 * person is left tapping at a door with no idea whether it opens in ten
 * seconds or a quarter of an hour.
 */
export function failureText(error: unknown, fallback: string): string {
  if (!(error instanceof ApiError)) return fallback;
  const wait = error.failure?.retry_after_seconds;
  if (wait === undefined) return error.message;
  return `Слишком много попыток. Ещё ${waitText(wait)}`;
}

/**
 * «авг 2026» — when a song was last sung, for the column down «Песни хора».
 *
 * `MONTHS_SHORT` is already three letters for all twelve months, so the cell is
 * one width at every value and a list of forty of them reads as a column that
 * does not move when one changes. That is the whole reason the date is here
 * rather than the elapsed time: «2 месяца назад», «год назад» and «3 года
 * назад» differ in length and in wording, and prose cannot be scanned down a
 * page. The regent also speaks in dates — «спели её в августе 26-го».
 *
 * The year is always printed, current or not, unlike `formatWheelDate`: telling
 * a year ago from a month ago is the one thing this figure exists for.
 */
export function formatMonthYear(iso: string): string {
  const when = new Date(iso);
  return `${MONTHS_SHORT[when.getMonth()]} ${when.getFullYear()}`;
}

/**
 * The dates a song was sung on, grouped into one line per year, newest first:
 * `{ year: '2026', dates: '23 авг · 14 июн · 8 мар' }`.
 *
 * A year is a heading down the left of the panel and its dates run across, so
 * the panel is a list of dates rather than a table of them. The input is
 * already newest first — the server orders it — and this keeps that order
 * inside a year as well as between years.
 */
export function datesByYear(isos: string[]): { year: string; dates: string }[] {
  const years: { year: string; dates: string[] }[] = [];
  for (const iso of isos) {
    const when = new Date(iso);
    const year = String(when.getFullYear());
    const day = `${when.getDate()} ${MONTHS_SHORT[when.getMonth()]}`;
    const last = years[years.length - 1];
    if (last !== undefined && last.year === year) last.dates.push(day);
    else years.push({ year, dates: [day] });
  }
  return years.map((one) => ({ year: one.year, dates: one.dates.join(' · ') }));
}
